import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue,
    useToast
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
import mySportSetIllustration from "assets/img/auth/mysportset.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { paramFromWindowLocation } from "../../utils/url-parser";
import { translate } from "../../i18n";
import { ApiError } from "../../apiClient";
import { landingPathForRole } from "../../routes/access-policy";
import { useAuth } from "./AuthProvider";
import { useAcceptOrganizationInvitation } from "./hooks/useAcceptOrganizationInvitation";
import { useAcceptInvitationAuthenticated } from "./hooks/useAcceptInvitationAuthenticated";
import { getUserRole } from "./roles";

function AcceptInvitationPage() {
    const invitationId = paramFromWindowLocation('invitationId');
    const organizationId = paramFromWindowLocation('organizationId');
    const inviteeEmail = paramFromWindowLocation('invitee');
    const { mutateAsync: acceptOrganizationInvitation, isLoading: isAccepting } = useAcceptOrganizationInvitation();
    const { mutateAsync: acceptInvitationAuthenticated, isLoading: isJoining } = useAcceptInvitationAuthenticated();
    const toast = useToast();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const { currentUser, signingIn, login, logout } = useAuth();
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = useColorModeValue("gray.400", "gray.500");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const [show, setShow] = useState(false);
    const handleClick = () => setShow(!show);

    const MIN_PASSWORD_LENGTH = 6;
    const linkIsValid = !!invitationId && !!organizationId && !!inviteeEmail;
    const canSubmit = linkIsValid && password.length >= MIN_PASSWORD_LENGTH;
    const currentEmail = (currentUser?.email as string) || '';

    // Guard against a malformed / truncated invite link: the three params come
    // straight from the URL and would otherwise be sent to the API as null.
    if (!linkIsValid)
        return (
            <DefaultAuth illustrationBackground={mySportSetIllustration} image={mySportSetIllustration}>
                <Flex h='100%' w={{ base: "100%", md: "50%" }} alignItems='center' justifyContent='center' px='25px'>
                    <Box>
                        <Heading color={textColor} fontSize='28px' mb='10px'>
                            {translate('auth.accept_invitation')}
                        </Heading>
                        <Text color={textColorSecondary} fontWeight='400' fontSize='md'>
                            {translate('auth.accept_invitation_error')}
                        </Text>
                    </Box>
                </Flex>
            </DefaultAuth>
        );

    // Signed in AS the invitee → instant one-click join, no password. (The
    // public accept endpoint refuses existing accounts; this authenticated
    // path is how an existing operator joins a new org.)
    if (currentUser && currentEmail.toLowerCase() === inviteeEmail.toLowerCase())
        return (
            <DefaultAuth illustrationBackground={mySportSetIllustration} image={mySportSetIllustration}>
                <Flex h='100%' w={{ base: "100%", md: "50%" }} alignItems='center' justifyContent='center' px='25px'>
                    <Box maxW='420px'>
                        <Heading color={textColor} fontSize='28px' mb='10px'>
                            {translate('auth.accept_invitation')}
                        </Heading>
                        <Text color={textColorSecondary} fontWeight='400' fontSize='md' mb='24px'>
                            {translate('auth.accept_invitation_signed_in_description', { email: currentEmail })}
                        </Text>
                        <Button
                            isLoading={isJoining}
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='100%'
                            h='50'
                            onClick={async () => {
                                try {
                                    const member = await acceptInvitationAuthenticated({ organizationId, invitationId });
                                    toast({ title: translate('auth.accept_invitation_success'), status: 'success', duration: 4000 });
                                    // Land on the role the SERVER just wrote, not on the
                                    // token's claim.
                                    //
                                    // This used to force a token refresh, on the stated
                                    // premise that "the accept call just changed this
                                    // account's custom claims server-side". It does not:
                                    // `accept-invitation-authenticated.ts` deliberately
                                    // never calls setCustomUserClaims, because the claim is
                                    // ONE global value per user and stamping `coach` here
                                    // would clobber the same person's admin claim in another
                                    // org. So the refresh returned identical claims, and a
                                    // freshly-invited coach with no claim (or an `admin`
                                    // claim from elsewhere) was sent to the admin landing —
                                    // exactly the "broken admin view full of 403s" the
                                    // comment claimed to prevent.
                                    //
                                    // The created membership's own `role` is authoritative
                                    // and needs no round-trip. Falling back to the claim
                                    // only if the response is somehow shapeless keeps this
                                    // no worse than before.
                                    navigate(landingPathForRole(member?.role ?? getUserRole(currentUser)), { replace: true });
                                } catch (error) {
                                    toast({
                                        title: error instanceof ApiError ? error.message : translate('auth.accept_invitation_error'),
                                        status: 'error',
                                        duration: 4000,
                                    });
                                }
                            }}>
                            {translate('auth.join_organization')}
                        </Button>
                    </Box>
                </Flex>
            </DefaultAuth>
        );

    // Signed in as a DIFFERENT account → the invite is not for this session.
    // Say so explicitly instead of silently bouncing to /admin/courts (the old
    // behavior, which dropped the invitation with zero feedback).
    if (currentUser)
        return (
            <DefaultAuth illustrationBackground={mySportSetIllustration} image={mySportSetIllustration}>
                <Flex h='100%' w={{ base: "100%", md: "50%" }} alignItems='center' justifyContent='center' px='25px'>
                    <Box maxW='420px'>
                        <Heading color={textColor} fontSize='28px' mb='10px'>
                            {translate('auth.accept_invitation')}
                        </Heading>
                        <Text color={textColorSecondary} fontWeight='400' fontSize='md' mb='24px'>
                            {translate('auth.accept_invitation_wrong_account', { email: inviteeEmail, currentEmail })}
                        </Text>
                        <Button
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='100%'
                            h='50'
                            onClick={logout}>
                            {translate('auth.logout')}
                        </Button>
                    </Box>
                </Flex>
            </DefaultAuth>
        );

    return (
        <DefaultAuth illustrationBackground={mySportSetIllustration} image={mySportSetIllustration}>
            <Flex
                maxW={{ base: "100%", md: "max-content" }}
                w='100%'
                mx={{ base: "auto", lg: "0px" }}
                me='auto'
                h='100%'
                alignItems='start'
                justifyContent='center'
                mb={{ base: "30px", md: "60px" }}
                px={{ base: "25px", md: "0px" }}
                mt={{ base: "40px", md: "14vh" }}
                flexDirection='column'>
                <Box me='auto'>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        {translate('auth.accept_invitation')}
                    </Heading>
                    <Text
                        mb='36px'
                        ms='4px'
                        color={textColorSecondary}
                        fontWeight='400'
                        fontSize='md'>
                        {translate('auth.accept_invitation_description')}
                    </Text>
                </Box>
                <Flex
                    zIndex='2'
                    direction='column'
                    w={{ base: "100%", md: "420px" }}
                    maxW='100%'
                    background='transparent'
                    borderRadius='15px'
                    mx={{ base: "auto", lg: "unset" }}
                    me='auto'
                    mb={{ base: "20px", md: "auto" }}>
                    <FormControl>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            mb='8px'>
                            {translate('auth.email')}<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                            value={inviteeEmail}
                            isRequired={true}
                            variant='auth'
                            isDisabled
                            fontSize='sm'
                            ms={{ base: "0px", md: "0px" }}
                            type='email'
                            placeholder='mail@simmmple.com'
                            mb='24px'
                            fontWeight='500'
                            size='lg'
                        />
                        <FormLabel
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            display='flex'>
                            {translate('auth.password')}<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <InputGroup size='md'>
                            <Input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                isRequired={true}
                                fontSize='sm'
                                placeholder='********'
                                mb='24px'
                                size='lg'
                                type={show ? "text" : "password"}
                                variant='auth'
                            />
                            <InputRightElement display='flex' alignItems='center' mt='4px'>
                                <Icon
                                    color={textColorSecondary}
                                    _hover={{ cursor: "pointer" }}
                                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                    onClick={handleClick}
                                />
                            </InputRightElement>
                        </InputGroup>
                        <Button
                            isLoading={signingIn || isAccepting}
                            isDisabled={!canSubmit}
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='100%'
                            h='50'
                            onClick={async () => {
                                try {
                                    await acceptOrganizationInvitation({
                                        email: inviteeEmail,
                                        password,
                                        invitationId,
                                        organizationId
                                    })
                                    await login(inviteeEmail, password);
                                } catch (error) {
                                    // The public flow refuses pre-existing accounts by
                                    // design (it would overwrite the password). Match the
                                    // server's specific message — other 409s exist (already
                                    // accepted / already a member) and need the generic
                                    // error, not the sign-in remedy.
                                    const accountExists = error instanceof ApiError
                                        && error.status === 409
                                        && error.message.toLowerCase().includes('account with this email already exists');
                                    toast({
                                        title: accountExists
                                            ? translate('auth.accept_invitation_account_exists')
                                            : translate('auth.accept_invitation_error'),
                                        status: 'error',
                                        duration: accountExists ? 8000 : 4000,
                                    });
                                }
                            }}
                            mb='24px'>
                            {translate('auth.join_organization')}
                        </Button>
                    </FormControl>
                </Flex>
            </Flex>
        </DefaultAuth>
    );
}

export default AcceptInvitationPage;
